/**
 * Shared chat answer sanitizer used by both ChatPage and the Learn right-side chat panel.
 *
 * This is intentionally sourced from the regular Chat page's `cleanLatex()` implementation
 * so LaTeX repair behavior (e.g. `rac{` -> `\\frac{`) stays consistent everywhere.
 */
export const sanitizeChatMarkdown = (content, { streaming = false } = {}) => {
  if (!content) return "";
  let str = Array.isArray(content) ? content.join("") : String(content);

  // TABLE DETECTION & CONVERSION (before other formatting)
  // Handles:
  // 1) Normal flattened tables with columns separated by 2+ spaces or tabs
  // 2) "Stacked" tables where cells appear one-per-line (common from PDFs)
  // 3) Tables mistakenly wrapped in ``` code fences (unwrap and render as table)
  // IMPORTANT (streaming): avoid table conversion while the answer is partial.
  // The heuristic can mis-detect spaced math as a "table" and introduce `| | --- | --- |`
  // separators mid-stream, which then flicker/reflow as more text arrives.
  if (!streaming) try {
    const lines = str.split(/\n/);
    const out = [];

    const isFence = (s) => /^```/.test((s || '').trim());
    const isMarkdownTableRow = (s) => {
      const t = (s || '').trim();
      return t.startsWith('|') && t.endsWith('|');
    };

    const splitByColumns = (line) => line.split(/\s{2,}|\t/).map(c => c.trim()).filter(Boolean);

    const looksMathy = (line) => {
      const t = String(line || '');
      // Explicit LaTeX tokens or inline math delimiters.
      if (/[\\$^_]/.test(t)) return true;
      // Common math operators/symbols in "spaced" form (often produced by renderers).
      if (/[=×÷±]/.test(t)) return true;
      // Very "tokenized" lines like "m 1 = 5.972 × 10 24 kg" are not tables.
      const cols = splitByColumns(t);
      if (cols.length >= 6) {
        const shortCols = cols.filter(c => (c || '').length <= 2).length;
        if (shortCols / cols.length > 0.5) return true;
      }
      return false;
    };

    const isPotentialTableRow = (line) => {
      if (!line) return false;
      if (isMarkdownTableRow(line)) return false;
      // Never treat math-heavy lines as a table row unless it is explicitly a markdown table.
      if (looksMathy(line)) return false;
      const cols = splitByColumns(line);
      if (cols.length < 2) return false;
      // If the split produced many columns, it's likely spaced math or prose.
      if (cols.length > 6) return false;
      // Require at least 2 columns with some letters to reduce false positives.
      const letterCols = cols.filter(c => /[A-Za-z]/.test(c)).length;
      if (letterCols < 2) return false;
      return true;
    };

    const looksLikeCellLine = (line) => {
      const t = (line || '').trim();
      if (!t) return false;
      if (isFence(t) || isMarkdownTableRow(t)) return false;
      if (/^#{1,6}\s+/.test(t) || /^>\s+/.test(t) || /^([\-*+]\s+|\d+\.\s+)/.test(t)) return false;
      if (t.length > 30) return false;
      if (/[.!?]\s*$/.test(t)) return false;
      // Most table cells have numbers/units/symbols; keep conservative
      return /\d/.test(t) || /\b(kg|g|N|m\/s\^?2|m\s*\/\s*s\^?2|cm|mm|km)\b/i.test(t);
    };

    const pushMarkdownTable = (headerCols, rows) => {
      if (!headerCols || headerCols.length < 2) return;
      out.push(`| ${headerCols.join(' | ')} |`);
      out.push(`| ${headerCols.map(() => '---').join(' | ')} |`);
      rows.forEach(r => {
        out.push(`| ${r.join(' | ')} |`);
      });
    };

    const tryConvertBlock = (blockLines) => {
      // 1) Standard multi-column rows (2+ spaces / tabs)
      if (blockLines.some(isPotentialTableRow)) {
        const tableRows = [];
        let idx = 0;
        while (idx < blockLines.length) {
          if (!isPotentialTableRow(blockLines[idx])) break;
          tableRows.push(blockLines[idx]);
          idx++;
        }
        if (tableRows.length >= 2) {
          const splitRows = tableRows.map(splitByColumns);
          // Only convert if column counts are consistent; otherwise it is likely not a table.
          const counts = splitRows.map(r => r.length);
          const minC = Math.min(...counts);
          const maxC = Math.max(...counts);
          if (maxC - minC > 1) {
            return { converted: null, consumed: 0, didConvert: false };
          }
          const headerCols = splitRows[0];
          const dataRows = splitRows.slice(1);
          return {
            converted: [
              `| ${headerCols.join(' | ')} |`,
              `| ${headerCols.map(() => '---').join(' | ')} |`,
              ...dataRows.map(r => `| ${r.join(' | ')} |`)
            ],
            consumed: idx,
            didConvert: true,
          };
        }
      }

      // 2) Stacked table: header line + subsequent cell lines
      // Very conservative trigger: header contains multiple "Weight on" columns
      // OR contains both Earth and Moon plus a leading "Mass" column.
      const header = (blockLines[0] || '').trim();
      const headerHasWeightOn = /\bWeight\s+on\b/i.test(header);
      const headerWeightOnCount = (header.match(/\bWeight\s+on\b/gi) || []).length;
      const headerHasEarthMoon = /\bEarth\b/i.test(header) && /\bMoon\b/i.test(header);
      const headerHasMass = /\bMass\b/i.test(header);

      let headerCols = null;
      if (headerHasWeightOn && headerWeightOnCount >= 2) {
        headerCols = header.split(/(?=\bWeight\s+on\b)/i).map(s => s.trim()).filter(Boolean);
      } else if (headerHasEarthMoon && headerHasMass) {
        // Fallback split for common "Earth vs Moon" wording
        headerCols = header
          .replace(/\s+vs\.?\s+/gi, ' | ')
          .split(/\s*\|\s*/)
          .map(s => s.trim())
          .filter(Boolean);
        if (headerCols.length < 3) {
          // As a last resort, split by known phrases
          const parts = header.split(/(?=\bWeight\b)/i).map(s => s.trim()).filter(Boolean);
          if (parts.length >= 3) headerCols = parts;
        }
      }

      if (headerCols && headerCols.length >= 3) {
        const colCount = headerCols.length;
        const cells = [];
        let idx = 1;
        while (idx < blockLines.length && looksLikeCellLine(blockLines[idx])) {
          cells.push(blockLines[idx].trim());
          idx++;
        }
        // Need at least 2 rows
        if (cells.length >= colCount * 2) {
          const rows = [];
          for (let c = 0; c + colCount - 1 < cells.length; c += colCount) {
            rows.push(cells.slice(c, c + colCount));
          }
          const converted = [
            `| ${headerCols.join(' | ')} |`,
            `| ${headerCols.map(() => '---').join(' | ')} |`,
            ...rows.map(r => `| ${r.join(' | ')} |`)
          ];
          return { converted, consumed: idx, didConvert: true };
        }
      }

      return { converted: null, consumed: 0, didConvert: false };
    };

    let i = 0;
    while (i < lines.length) {
      // Unwrap table-like code fences so Markdown can render tables.
      if (isFence(lines[i])) {
        const fenceStart = i;
        i++;
        const block = [];
        while (i < lines.length && !isFence(lines[i])) {
          block.push(lines[i]);
          i++;
        }
        const hasFenceEnd = i < lines.length && isFence(lines[i]);
        if (hasFenceEnd) i++; // consume closing fence

        const attempted = tryConvertBlock(block);
        if (attempted.didConvert && attempted.converted) {
          out.push(...attempted.converted);
        } else {
          // Keep original code block if it wasn't a table.
          out.push(lines[fenceStart]);
          out.push(...block);
          if (hasFenceEnd) out.push(lines[i - 1]);
        }
        continue;
      }

      // Normal line: try standard flattened table conversion for consecutive rows.
      if (isPotentialTableRow(lines[i])) {
        const tableRows = [];
        while (i < lines.length && isPotentialTableRow(lines[i])) {
          tableRows.push(lines[i]);
          i++;
        }
        if (tableRows.length >= 2) {
          const splitRows = tableRows.map(splitByColumns);
          const counts = splitRows.map(r => r.length);
          const minC = Math.min(...counts);
          const maxC = Math.max(...counts);
          if (maxC - minC <= 1) {
          const headerCols = splitRows[0];
          const dataRows = splitRows.slice(1);
          pushMarkdownTable(headerCols, dataRows);
          continue;
          }
        }
        // If we collected <2 rows, fall through and output raw.
        out.push(...tableRows);
        continue;
      }

      // Normal line: stacked table (header + cell lines)
      const header = (lines[i] || '').trim();
      const headerHasWeightOn = /\bWeight\s+on\b/i.test(header);
      const headerWeightOnCount = (header.match(/\bWeight\s+on\b/gi) || []).length;
      const headerHasEarthMoon = /\bEarth\b/i.test(header) && /\bMoon\b/i.test(header);
      const headerHasMass = /\bMass\b/i.test(header);

      if ((headerHasWeightOn && headerWeightOnCount >= 2) || (headerHasEarthMoon && headerHasMass)) {
        const block = lines.slice(i, Math.min(lines.length, i + 40));
        const attempted = tryConvertBlock(block);
        if (attempted.didConvert && attempted.converted && attempted.consumed > 0) {
          out.push(...attempted.converted);
          i += attempted.consumed;
          continue;
        }
      }

      out.push(lines[i]);
      i++;
    }

    str = out.join('\n');
  } catch {
    /* If table conversion fails, keep original string. */
  }

  // 1. KILL RED BOXES (Control Characters)
  str = str.replace(/[\u0000-\u0009\u000B\u000C\u000E-\u001F]/g, "");
  str = str.replace(/\\f/g, "");

  // 2. Fix Double Escaping & Junk
  str = str.replace(/\\\\text/g, "\\text");
  str = str.replace(/\\\\frac/g, "\\frac");
  str = str.replace(/\\\\times/g, "\\times");
  str = str.replace(/(?<!\\)\bext\{/g, "\\text{");
  str = str.replace(/(?<!\\)\bimes\b/g, "\\times");
  str = str.replace(/(?<!\\)\brac\{/g, "\\frac{");

  // Protect escaped spaces by explicitly keeping them as `\ ` instead of `~`
  // `~` can collide with remarkGfm strikethrough parser in edge cases, destroying the Math AST.
  str = str.replace(/\\ \s*/g, '\\ ');

  // proactively replace any `~` generated by the LLM inside KaTeX blocks with a safe `\ ` KaTeX space.
  str = str.replace(/~(?=\\text)/g, '\\ ');
  str = str.replace(/~/g, '\\ ');

  // 3. NORMALIZE DELIMITERS

  // Specific fix: unexpected \tag in inline math -> force display math
  // (Katex errors if \tag is used in inline mode)
  str = str.replace(/\\\(([\s\S]*?\\tag\s*\{[\s\S]*?\})\\\)/g, '$$$$$1$$$$');
  str = str.replace(/(?<!\$)\$(?!\$)([^\$]*?\\tag\s*\{[^\}]+\}[^\$]*?)(?<!\$)\$(?!\$)/g, '$$$$$1$$$$');

  str = str.replace(/\\\[([\s\S]*?)\\\]/g, '$$$$$1$$$$');
  str = str.replace(/\\\(/g, '$');
  str = str.replace(/\\\)/g, '$');



  // 3.5. WRAP ISOLATED \text{...} (Fix for units/labels outside math)
  // Detects \text{...} that is NOT inside $...$ or $$...$$ and wraps it in $.
  // This ensures units like "$5$ \text{kg}" render as "$5$ $\text{kg}$".
  str = str.replace(/(\$\$[\s\S]*?\$\$|\$[^$]*?\$)|(\\text\{[^}]+\})/g, (match, mathGroup, textGroup) => {
    if (mathGroup) return match; // It's inside math, leave it alone
    return `$${match}$`; // Wrap isolated \text{...} in $
  });

  // 4. ✨ SPACING INJECTOR (The Fix for "is$M" or "kg$and")
  // Add space before inline $ if preceded by a letter/number
  str = str.replace(/([a-zA-Z0-9%,.?!])\$/g, '$1 $');
  // Add space after inline $ if followed by a letter/number
  str = str.replace(/\$([a-zA-Z0-9])/g, '$ $1');
  // Prevent removing adjacent inline math groups via a dangerous $$ replace.
  // We ONLY want to fix `$a$$b$` which is very rare, and destroying `$$ block $$` is far worse.
  // So we remove the old aggressive replace: `str = str.replace(/\$\$([^\$])/g, '$ $1');`

  // 5. 🏗️ STRUCTURE FORMATTER (The "Textbook" Layout)
  // Force Block Math ($$ ... $$) to sit on its own lines
  // This creates the visual "Step-by-Step" vertical flow
  str = str.replace(/([^\n])\$\$/g, '$1\n\n$$'); // Newline before block
  str = str.replace(/\$\$([^\n])/g, '$$\n\n$1'); // Newline after block

  // 5.2 DEPRECATED (Moved to remarkLatexSanitizer AST plugin)
  // 5.3 DEPRECATED (Moved to remarkLatexSanitizer AST plugin)

  // Strip leading and trailing spaces immediately inside inline math delimiters.
  // remarkMath will NOT parse "$ W = m \times g $" as math, it requires "$W = m \times g$".
  // Note: Needs to run AFTER Step 4 spacing injectors to prevent them from re-breaking the trim.
  str = str.replace(/(?<!\$)\$([^\$]+)\$(?!\$)/g, (match, inner) => {
    return `$${inner.trim()}$`;
  });

  // 5.5 DEPRECATED (Prompt heavily guarantees boxing and KaTeX wrapping)

  // 6. 🪄 HEADER PROMOTION (UI polish)
  // Many LLM responses return section titles as plain lines (no leading '#').
  // Promote obvious section-title lines into Markdown headings so the UI can
  // style them like the light-theme reference.
  try {
    const lines = str.split(/\n/);
    const promoted = [];

    for (let i = 0; i < lines.length; i++) {
      const raw = lines[i];
      const t = raw.trim();

      if (!t) {
        promoted.push(raw);
        continue;
      }

      // Don’t touch existing markdown headings, code fences, or list items.
      if (/^#{1,6}\s+/.test(t) || /^```/.test(t) || /^[\-*+]\s+/.test(t) || /^\d+\.\s+/.test(t) || /^>\s+/.test(t)) {
        promoted.push(raw);
        continue;
      }

      const prevBlank = i === 0 || !lines[i - 1].trim();
      const nextBlank = i === lines.length - 1 || !lines[i + 1].trim();
      const endsWithSentencePunct = /[.!?]\s*$/.test(t);
      const wordCount = t.split(/\s+/).filter(Boolean).length;
      const hasColon = t.includes(':');
      const looksLikeTitle = /^[A-Z][A-Za-z0-9 ,.'()\/-]+$/.test(t);
      const isShortEnough = t.length >= 4 && t.length <= 80;

      // Strong heuristic: a standalone, title-like line between blank lines.
      // Require either a ':' (e.g., "Mathematical Foundation: Eq (9.9)")
      // OR a short title (<= 8 words) to avoid turning normal sentences into headings.
      const shouldPromote = prevBlank && nextBlank && isShortEnough && !endsWithSentencePunct && looksLikeTitle && (hasColon || wordCount <= 8);

      if (shouldPromote) {
        // Make it a level-2 heading for consistent styling.
        promoted.push(`## ${t}`);
      } else {
        promoted.push(raw);
      }
    }

    str = promoted.join('\n');
  } catch {
    // If promotion fails, keep original string.
  }

  // 7. • LIST PROMOTION (definitions + example blocks)
  // Turn blocks of consecutive definition-like lines or example fragments into
  // markdown bullet lists. This intentionally does NOT rely on trigger words
  // like "where:" because LLM responses vary.
  try {
    const lines = str.split(/\n/);
    const out = [];

    const isFence = (s) => /^```/.test(s.trim());
    const isExistingListItem = (s) => /^\s*([\-*+]\s+|\d+\.\s+)/.test(s);
    const isHeadingLike = (s) => /^\s*#{1,6}\s+/.test(s.trim());
    const isQuoteLike = (s) => /^\s*>\s+/.test(s.trim());
    const isTableRow = (s) => {
      const t = s.trim();
      return t.startsWith('|') && t.endsWith('|');
    };

    // A "definition" line typically starts with a short term/variable then "is/are".
    // Examples:
    // - F is the gravitational force
    // - G is the universal gravitational constant
    // - m_1 and m_2 are the masses...
    // - $r$ is the distance...
    const isDefinitionLine = (raw) => {
      const t = raw.trim();
      if (!t) return false;
      if (isExistingListItem(raw)) return false;
      if (isHeadingLike(raw) || isQuoteLike(raw)) return false;
      if (isTableRow(raw)) return false;
      if (t.length > 140) return false;

      // Extract the left-most "term" token (up to first space)
      const firstTokenRaw = t.split(/\s+/)[0] || '';
      const firstToken = firstTokenRaw.replace(/[,:;.)\]]+$/g, '');
      const tokenIsMath = /^\$[^$]{1,30}\$$/.test(firstToken);
      const tokenIsVar = /^[A-Za-z][A-Za-z0-9_]*$/.test(firstToken);
      const tokenIsVarWithSub = /^[A-Za-z]_(\d+|\{\d+\})$/.test(firstToken) || /^[A-Za-z]\{?\d+\}?$/.test(firstToken);
      // Unicode subscript digits: ₀₁₂₃₄₅₆₇₈₉ (common in rendered answers)
      const tokenIsUnicodeSub = /^[A-Za-z][₀₁₂₃₄₅₆₇₈₉]+$/.test(firstToken);
      const tokenIsGreekLike = /^\\[a-zA-Z]{2,20}$/.test(firstToken); // e.g. \theta
      const termOk = tokenIsMath || tokenIsVar || tokenIsVarWithSub || tokenIsUnicodeSub || tokenIsGreekLike;
      if (!termOk) return false;

      // Must read like a legend/definition.
      return /^([^\n]{0,40})\b(is|are)\b/i.test(t);
    };

    // Example fragment lines often appear as short, standalone lines after an intro
    // like "such as:" OR as indented blocks (which Tailwind/Markdown may otherwise
    // treat weirdly). Convert runs of these into bullet lists.
    const isIndentedFragment = (raw) => {
      if (!/^\s{2,}\S/.test(raw)) return false;
      const t = raw.trim();
      if (!t) return false;
      if (isExistingListItem(raw)) return false;
      if (isHeadingLike(raw) || isQuoteLike(raw)) return false;
      if (isTableRow(raw)) return false;
      if (t.length > 90) return false;
      // Avoid accidentally converting math blocks
      if (t.startsWith('$$') || t.startsWith('$') || t.startsWith('\\')) return false;
      return true;
    };

    const isExampleFragment = (raw) => {
      const t = raw.trim();
      if (!t) return false;
      if (isExistingListItem(raw)) return false;
      if (isHeadingLike(raw) || isQuoteLike(raw)) return false;
      if (isTableRow(raw)) return false;
      if (t.length > 90) return false;
      // Keep fragments (not full sentences)
      if (/[.!?]\s*$/.test(t)) return false;
      const wc = t.split(/\s+/).filter(Boolean).length;
      if (wc < 2 || wc > 14) return false;
      // Avoid converting labels like "where:" or "Formula:"
      if (/^[A-Za-z][A-Za-z ]{0,20}:\s*$/.test(t)) return false;
      return true;
    };

    let inCodeFence = false;
    for (let i = 0; i < lines.length; i++) {
      const raw = lines[i];
      const t = raw.trim();

      if (isFence(raw)) {
        inCodeFence = !inCodeFence;
        out.push(raw);
        continue;
      }
      if (inCodeFence) {
        out.push(raw);
        continue;
      }

      // Detect a run of consecutive definition lines.
      if (isDefinitionLine(raw)) {
        const runStart = i;
        let runEnd = i;
        while (runEnd + 1 < lines.length && isDefinitionLine(lines[runEnd + 1])) {
          runEnd++;
        }

        const runLen = runEnd - runStart + 1;
        if (runLen >= 2) {
          for (let j = runStart; j <= runEnd; j++) {
            out.push(`- ${lines[j].trim()}`);
          }
          i = runEnd;
          continue;
        }
      }

      // Detect runs of indented fragments (common in LLM outputs).
      if (isIndentedFragment(raw)) {
        const runStart = i;
        let runEnd = i;
        while (runEnd + 1 < lines.length && isIndentedFragment(lines[runEnd + 1])) {
          runEnd++;
        }
        const runLen = runEnd - runStart + 1;
        if (runLen >= 2) {
          for (let j = runStart; j <= runEnd; j++) {
            out.push(`- ${lines[j].trim()}`);
          }
          i = runEnd;
          continue;
        }
      }

      // Detect "intro:" then consecutive example fragments.
      if (t && /:\s*$/.test(t) && !isHeadingLike(raw) && !isQuoteLike(raw) && !isExistingListItem(raw) && !isTableRow(raw)) {
        let j = i + 1;
        const fragIdxs = [];
        while (j < lines.length) {
          const next = lines[j];
          const nt = next.trim();
          if (!nt) break;
          if (isFence(next) || isHeadingLike(next) || isQuoteLike(next) || isTableRow(next)) break;
          if (isExampleFragment(next)) {
            fragIdxs.push(j);
            j++;
            continue;
          }
          break;
        }
        if (fragIdxs.length >= 2) {
          out.push(raw);
          for (const idx of fragIdxs) out.push(`- ${lines[idx].trim()}`);
          i = fragIdxs[fragIdxs.length - 1];
          continue;
        }
      }

      out.push(raw);
    }

    str = out.join('\n');
  } catch {
    // If list promotion fails, keep original string.
  }

  return str;
};
